import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../common/modal';
import Spinner from '../common/spinner';
import Searchbar from '../common/searchbar';
import { DropdownPill } from '../common/dropdown-pill';
import { Paginator } from '../common/paginator';
import { ListenerAudio, ListenerAudioResponse, useBrowseListenerAudio } from '../../hooks/useListenerAudio';
import { Resource, useBrowseResources } from '../../hooks/useResources';
import AudioPlayer from '../common/audio-player';
import { Tag } from '../../hooks/useTags';
import { use2026TagGroups } from '../../hooks/useTagGroups';
import { useDomains } from '../../hooks/useClients';
import { TagList, formatEnabledFor, Truncate } from '../../containers/content-library/components/common';
import {
  buildAudioParams,
  buildVideoParams,
  buildResourcesParams,
} from '../../containers/content-library/utils/browse-params';
import { getResourceTagNames, getListenerAudioTagNames } from '../../containers/content-library/utils/tag-helpers';

const AUDIO_LIMIT = 10;
const VIDEO_LIMIT = 10;
const RESOURCE_LIMIT = 10;

type CollectionModalTab = 'audio' | 'video' | 'resource';

const tabs: { key: CollectionModalTab; label: string }[] = [
  { key: 'audio', label: 'Audio' },
  { key: 'video', label: 'Video' },
  { key: 'resource', label: 'Resources' },
];

export type AddableCollectionItem =
  | { type: 'audio'; experience: ListenerAudio }
  | { type: 'video'; experience: ListenerAudio }
  | { type: 'resource'; resource: Resource };

interface AddCollectionItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: AddableCollectionItem) => Promise<void> | void;
  existingListenerAudioIds: number[];
  existingResourceIds: number[];
  isBusy?: boolean;
  audioTagMap?: Map<number, Tag>;
  resourceTagMap?: Map<number, Tag>;
}

const AddCollectionItemModal: React.FC<AddCollectionItemModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  existingListenerAudioIds,
  existingResourceIds,
  isBusy = false,
  audioTagMap,
  resourceTagMap,
}) => {
  const [activeTab, setActiveTab] = useState<CollectionModalTab>('audio');
  const [audioPage, setAudioPage] = useState(1);
  const [videoPage, setVideoPage] = useState(1);
  const [resourcePage, setResourcePage] = useState(1);
  const [selectedTagGroupId, setSelectedTagGroupId] = useState<number | undefined>(undefined);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [selectedDomainId, setSelectedDomainId] = useState<number | undefined>(undefined);
  const [searchTerms, setSearchTerms] = useState<{ audio: string; video: string; resource: string }>({
    audio: '',
    video: '',
    resource: '',
  });

  const { data: topicTagGroups } = use2026TagGroups();

  const { data: domains } = useDomains();
  const domainOptions = useMemo(
    () => (domains ?? []).map((d) => ({ value: d.id, label: `${d.name} (#${d.id})` })),
    [domains],
  );

  useEffect(() => {
    if (isOpen) {
      setActiveTab('audio');
      setAudioPage(1);
      setVideoPage(1);
      setResourcePage(1);
      setSelectedTagGroupId(undefined);
      setSelectedTagIds([]);
      setSelectedDomainId(undefined);
      setSearchTerms({ audio: '', video: '', resource: '' });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!selectedTagGroupId) {
      setSelectedTagIds([]);
    }
  }, [selectedTagGroupId]);

  const resetPageForTab = useCallback(
    (tab?: CollectionModalTab) => {
      const t = tab ?? activeTab;
      if (t === 'audio') setAudioPage(1);
      else if (t === 'video') setVideoPage(1);
      else if (t === 'resource') setResourcePage(1);
    },
    [activeTab],
  );

  const handleTagGroupChange = (tagGroupId: number | undefined) => {
    setSelectedTagGroupId(tagGroupId);
    setSelectedTagIds([]);
    resetPageForTab();
  };

  const handleDomainChange = (domainId: number | undefined) => {
    setSelectedDomainId(domainId);
    resetPageForTab();
  };

  const sharedFilters = useMemo(
    () => ({
      tagGroupIds: selectedTagGroupId != null ? [selectedTagGroupId] : [],
      tagIds: selectedTagIds,
      domainId: selectedDomainId ?? null,
      clientId: null,
    }),
    [selectedTagGroupId, selectedTagIds, selectedDomainId],
  );

  const audioParams = useMemo(
    () => buildAudioParams(audioPage, searchTerms.audio, sharedFilters, AUDIO_LIMIT, false),
    [audioPage, searchTerms.audio, sharedFilters],
  );

  const videoParams = useMemo(
    () => buildVideoParams(videoPage, searchTerms.video, sharedFilters, VIDEO_LIMIT, false),
    [videoPage, searchTerms.video, sharedFilters],
  );

  const resourcesParams = useMemo(
    () => buildResourcesParams(resourcePage, searchTerms.resource, sharedFilters, RESOURCE_LIMIT, ['live', 'hidden']),
    [resourcePage, searchTerms.resource, sharedFilters],
  );

  const { data: audioData, isLoading: isAudioLoading } = useBrowseListenerAudio(audioParams);
  const { data: videoData, isLoading: isVideoLoading } = useBrowseListenerAudio(videoParams);
  const { data: resourceDataResponse, isLoading: isResourceLoading } = useBrowseResources(resourcesParams);

  const resourceData = resourceDataResponse?.data;
  const resourceCount = resourceDataResponse?.count ?? 0;

  useEffect(() => {
    if (audioData?.count !== undefined) {
      const maxPage = Math.max(1, Math.ceil((audioData.count || 0) / AUDIO_LIMIT) || 1);
      if (audioPage > maxPage) {
        setAudioPage(maxPage);
      }
    }
  }, [audioData?.count, audioPage]);

  useEffect(() => {
    if (videoData?.count !== undefined) {
      const maxPage = Math.max(1, Math.ceil((videoData.count || 0) / VIDEO_LIMIT) || 1);
      if (videoPage > maxPage) {
        setVideoPage(maxPage);
      }
    }
  }, [videoData?.count, videoPage]);

  useEffect(() => {
    if (resourceDataResponse?.count !== undefined) {
      const maxPage = Math.max(1, Math.ceil((resourceDataResponse.count || 0) / RESOURCE_LIMIT) || 1);
      if (resourcePage > maxPage) {
        setResourcePage(maxPage);
      }
    }
  }, [resourceDataResponse?.count, resourcePage]);

  const existingAudioIdSet = useMemo(() => new Set(existingListenerAudioIds), [existingListenerAudioIds]);
  const existingResourceIdSet = useMemo(() => new Set(existingResourceIds), [existingResourceIds]);

  const handleSearch = (tab: CollectionModalTab, value: string) => {
    setSearchTerms((prev) => ({ ...prev, [tab]: value }));
    resetPageForTab(tab);
  };

  const handleAddExperience = async (experience: ListenerAudio, type: 'audio' | 'video') => {
    await onSelect({ type, experience });
  };

  const handleAddResource = async (resource: Resource) => {
    await onSelect({ type: 'resource', resource });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      isModel={true}
      InnerComponent={
        <div
          className="inline-block w-full transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:max-w-5xl sm:align-middle"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">Add Collection Item</h3>
            <button className="text-blue-primary text-sm font-semibold" onClick={onClose}>
              Close
            </button>
          </div>

          <div className="px-6 py-4">
            <div className="flex border-b border-gray-200">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    className={`mr-4 border-b-2 px-4 py-2 text-sm font-semibold ${
                      isActive ? 'border-blue-primary text-blue-primary' : 'border-transparent text-gray-500'
                    }`}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="w-full max-w-sm">
                <Searchbar key={activeTab} search={(value) => handleSearch(activeTab, value)} />
              </div>
              <DropdownPill
                label="Filter by topic"
                placeholder="Filter by topic"
                options={topicTagGroups?.map((tg) => ({ value: tg.id, label: tg.name })) || []}
                selectedValue={selectedTagGroupId}
                onSelect={(value) => handleTagGroupChange(value as number | undefined)}
              />
              <DropdownPill
                label="Filter by domain"
                placeholder="Filter by domain"
                options={domainOptions}
                selectedValue={selectedDomainId}
                onSelect={(value) => handleDomainChange(value as number | undefined)}
              />
            </div>

            <div className="mt-6">
              {activeTab === 'audio' &&
                renderExperienceSection({
                  type: 'audio',
                  data: audioData,
                  isLoading: isAudioLoading,
                  page: audioPage,
                  limit: AUDIO_LIMIT,
                  onPageChange: setAudioPage,
                  existingIds: existingAudioIdSet,
                  onAdd: handleAddExperience,
                  isBusy,
                  tagMap: audioTagMap,
                })}

              {activeTab === 'video' &&
                renderExperienceSection({
                  type: 'video',
                  data: videoData,
                  isLoading: isVideoLoading,
                  page: videoPage,
                  limit: VIDEO_LIMIT,
                  onPageChange: setVideoPage,
                  existingIds: existingAudioIdSet,
                  onAdd: handleAddExperience,
                  isBusy,
                  tagMap: audioTagMap,
                })}

              {activeTab === 'resource' && (
                <ResourceList
                  rows={resourceData}
                  isLoading={isResourceLoading}
                  count={resourceCount}
                  currentPage={resourcePage}
                  limit={RESOURCE_LIMIT}
                  onPageChange={setResourcePage}
                  existingIds={existingResourceIdSet}
                  onAdd={handleAddResource}
                  isBusy={isBusy}
                  tagMap={resourceTagMap}
                />
              )}
            </div>
          </div>
        </div>
      }
    />
  );
};

interface ExperienceSectionProps {
  type: 'audio' | 'video';
  data?: ListenerAudioResponse;
  isLoading: boolean;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  existingIds: Set<number>;
  onAdd: (experience: ListenerAudio, type: 'audio' | 'video') => Promise<void>;
  isBusy: boolean;
  tagMap?: Map<number, Tag>;
}

const renderExperienceSection = ({
  type,
  data,
  isLoading,
  page,
  limit,
  onPageChange,
  existingIds,
  onAdd,
  isBusy,
  tagMap,
}: ExperienceSectionProps) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  const rows = data?.data || [];

  if (!rows.length) {
    return <EmptyState message={`No ${type === 'audio' ? 'audio' : 'video'} content found.`} />;
  }

  const showAudioPlayer = type === 'audio';
  const showVideoLink = type === 'video';

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse">
          <thead>
            <tr className="bg-gray-100 text-left text-xs uppercase text-gray-500">
              {showAudioPlayer && <th className="w-28 px-4 py-4">Play</th>}
              <th className="w-16 px-4 py-4">Image</th>
              <th className="w-48 px-4 py-4">Title</th>
              <th className="w-36 px-4 py-4">Listener</th>
              <th className="w-28 px-4 py-4">Domain</th>
              <th className="w-20 px-4 py-4">Role ID</th>
              <th className="w-20 px-4 py-4">Views</th>
              <th className="w-32 px-4 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((experience) => {
              const listenerName =
                experience.listener_role?.user?.display_name || experience.listener_role?.user?.first_name || 'Unknown';
              const isAlreadyAdded = existingIds.has(experience.id);
              const profilePhoto = experience.profile_photo_square_file_url;
              const tagNames = getListenerAudioTagNames(experience, tagMap);
              const colCount = showAudioPlayer ? 8 : 7;

              return (
                <React.Fragment key={experience.id}>
                  <tr className="text-sm text-gray-700">
                    {showAudioPlayer && (
                      <td className="px-4 py-4">
                        <AudioPlayer
                          url={experience.file_url}
                          isTimeDisplay={true}
                          audioDuration={experience.duration}
                        />
                      </td>
                    )}
                    <td className="px-4 py-4">
                      {profilePhoto ? (
                        <img src={profilePhoto} alt={listenerName} className="h-10 w-10 rounded-full object-cover" />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-xs text-gray-500">
                          —
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 align-top">
                      <Truncate title={experience.title || `Experience #${experience.id}`} className="font-semibold">
                        {experience.title || `Experience #${experience.id}`}
                      </Truncate>
                      <div className="text-xs text-gray-500">
                        #{experience.id}
                        {showVideoLink && experience.video_id && (
                          <>
                            {' · '}
                            <a
                              href={`https://vimeo.com/${experience.video_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-primary"
                            >
                              Watch Video
                            </a>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <Truncate title={listenerName}>{listenerName}</Truncate>
                    </td>
                    <td className="px-4 py-4 text-sm">
                      <Truncate title={experience.listener_role?.domain?.name ?? undefined}>
                        {experience.listener_role?.domain?.name ?? '—'}
                      </Truncate>
                    </td>
                    <td className="px-4 py-4 font-mono text-xs">{experience.listener_role_id}</td>
                    <td className="px-4 py-4 font-semibold">{experience.total_playbacks ?? 0}</td>
                    <td className="px-4 py-4 text-right">
                      <button
                        className={`rounded-md px-3 py-1 text-xs font-semibold ${
                          isAlreadyAdded ? 'cursor-not-allowed bg-gray-200 text-gray-500' : 'bg-blue-primary text-white'
                        }`}
                        onClick={() => onAdd(experience, type)}
                        disabled={isAlreadyAdded || isBusy}
                      >
                        {isAlreadyAdded ? 'In Collection' : 'Add'}
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={colCount} className="border-b-2 border-gray-200 px-4 py-3">
                      <TagList tags={tagNames} />
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-end">
        <Paginator count={data?.count ?? 0} currentPage={page} limit={limit} handlePageChange={onPageChange} />
      </div>
    </>
  );
};

interface ResourceListProps {
  rows?: Resource[];
  isLoading: boolean;
  count?: number;
  currentPage: number;
  limit: number;
  onPageChange: (page: number) => void;
  existingIds: Set<number>;
  onAdd: (resource: Resource) => Promise<void>;
  isBusy: boolean;
  tagMap?: Map<number, Tag>;
}

const ResourceList: React.FC<ResourceListProps> = ({
  rows,
  isLoading,
  count,
  currentPage,
  limit,
  onPageChange,
  existingIds,
  onAdd,
  isBusy,
  tagMap,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (!rows) {
    return <EmptyState message="No resources found." />;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full table-fixed border-collapse">
          <thead>
            <tr className="bg-gray-100 text-left text-xs uppercase text-gray-500">
              <th className="w-56 px-4 py-4">Name</th>
              <th className="w-28 px-4 py-4">Client</th>
              <th className="w-40 px-4 py-4">Enabled For</th>
              <th className="w-20 px-4 py-4">Status</th>
              <th className="w-16 px-4 py-4">Sort</th>
              <th className="w-32 px-4 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((resource) => {
              const isAlreadyAdded = resource.id ? existingIds.has(resource.id) : false;
              const tagNames = getResourceTagNames(resource, tagMap);
              return (
                <React.Fragment key={resource.id || resource.url}>
                  <tr className="text-sm text-gray-700">
                    <td className="px-4 py-4 align-top">
                      <Truncate title={resource.name} className="font-semibold">
                        {resource.name}
                      </Truncate>
                      <Truncate title={resource.url} className="mt-0.5 text-xs text-gray-500">
                        {resource.url}
                      </Truncate>
                    </td>
                    <td className="px-4 py-4">
                      {resource.client_id ? (
                        <div
                          className="min-w-0 truncate rounded bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700"
                          title={`${resource.client_id}: ${resource.client_name}`}
                        >
                          {resource.client_id}: {resource.client_name}
                        </div>
                      ) : (
                        <span className="rounded bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700">Global</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm">{formatEnabledFor(resource.enabled_for, resource.client_id)}</td>
                    <td className="px-4 py-4 capitalize">{resource.status}</td>
                    <td className="px-4 py-4">{resource.sort_weight}</td>
                    <td className="px-4 py-4 text-right">
                      <button
                        className={`rounded-md px-3 py-1 text-xs font-semibold ${
                          isAlreadyAdded ? 'cursor-not-allowed bg-gray-200 text-gray-500' : 'bg-blue-primary text-white'
                        }`}
                        onClick={() => onAdd(resource)}
                        disabled={isAlreadyAdded || isBusy}
                      >
                        {isAlreadyAdded ? 'In Collection' : 'Add'}
                      </button>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={6} className="border-b-2 border-gray-200 px-4 py-3">
                      <TagList tags={tagNames} />
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-end">
        {count && <Paginator count={count} currentPage={currentPage} limit={limit} handlePageChange={onPageChange} />}
      </div>
    </>
  );
};

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="rounded-md border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">
    {message}
  </div>
);

export default AddCollectionItemModal;
